import { useToggleState } from "react-stately";
import React from "react";
import {
  AriaCheckboxProps,
  useCheckbox,
  VisuallyHidden,
  useFocusRing,
  useCheckboxGroupItem,
  AriaCheckboxGroupItemProps,
} from "react-aria";
import { GroupContext } from "./checkbox-group";
import { Box } from "../box";
import { Color } from "../../utils/theme";
import { cx } from "../../utils/dom";
import { StyleProps } from "../../utils/types";

export interface CheckboxVariantProps
  extends StyleProps<"wrapper" | "icon" | "checkbox"> {
  appearance?: "outline" | "filled";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  shape?: "sharp" | "round";
  color?: Color;
}
export interface CheckboxProps extends AriaCheckboxProps, CheckboxVariantProps {
  className?: string;
}

export const Checkbox = React.forwardRef<HTMLLabelElement, CheckboxProps>(
  (props, ref) => {
    const {
      appearance,
      size,
      shape = "round",
      color,
      className,
      classNames,
      styles,
      style,
      ...otherProps
    } = props;

    // check if checkbox is rendered in a group context
    const groupContext = React.useContext(GroupContext);
    const groupState = groupContext?.state;
    const groupProps = groupContext?.props;
    const isWithinGroup = groupState != null;

    const singleState = useToggleState(props);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const single = useCheckbox(otherProps, singleState, inputRef);
    const group =
      groupState &&
      useCheckboxGroupItem(
        otherProps as AriaCheckboxGroupItemProps,
        groupState,
        inputRef
      );

    const { isFocusVisible, focusProps } = useFocusRing();

    const inputProps = isWithinGroup ? group?.inputProps : single.inputProps;
    const state = isWithinGroup ? groupState : singleState;
    const isSelected = isWithinGroup
      ? groupState.isSelected(props.value ?? "")
      : state.isSelected;

    function getRadius() {
      const resolvedShape = shape || groupProps?.shape || undefined;

      if (resolvedShape === "sharp") {
        return "none";
      }

      if (resolvedShape === "round") {
        return "md";
      }

      return undefined;
    }

    const resolvedColor = color || groupProps?.color || undefined;
    const resolvedAppearance =
      appearance || groupProps?.appearance || "outline";
    const resolvedSize = size || groupProps?.size || "md";

    return (
      <label
        style={{ ...style, ...styles?.wrapper }}
        className={cx(className, "p-checkbox", classNames?.wrapper)}
        data-disabled={inputProps?.disabled}
        ref={ref}
      >
        <VisuallyHidden>
          <input
            type="checkbox"
            ref={inputRef}
            {...inputProps}
            {...focusProps}
          />
        </VisuallyHidden>
        <div
          style={{ ...style, ...styles?.checkbox }}
          data-color={resolvedColor}
          data-radius={getRadius()}
          className={cx(["p-fake-checkbox", classNames?.checkbox])}
          data-focused={isFocusVisible}
          aria-hidden="true"
          data-checked={isSelected && !props.isIndeterminate}
          data-indeterminate={props.isIndeterminate}
          data-size={resolvedSize}
          data-appearance={resolvedAppearance}
        >
          {isSelected && !props.isIndeterminate && (
            <svg
              className={cx(["p-checkbox-icon", classNames?.icon])}
              stroke="white"
              width={"0.75rem"}
              height={"0.75rem"}
              viewBox="0 0 18 18"
            >
              <polyline
                points="1 9 7 14 15 4"
                fill="none"
                strokeWidth={3}
                strokeDasharray={22}
                strokeDashoffset={isSelected ? 44 : 66}
                style={{
                  transition: "all 400ms",
                }}
              />
            </svg>
          )}

          {props.isIndeterminate && (
            <Box
              className={cx(["p-checkbox-icon", classNames?.icon])}
              style={{
                background: "var(--color-12)",
                width: "70%",
                height: 2,
                borderRadius: 4,
              }}
            ></Box>
          )}
        </div>
        {otherProps.children}
      </label>
    );
  }
);

Checkbox.displayName = "checkbox";
